import { useMemo, type ReactNode } from 'react';
import { Check, HelpCircle, MessagesSquare } from 'lucide-react';

import type { KnowledgeParticle } from '../types/domain';
import { extractStructuredTextParts, hasExplicitStructuredMain } from '../utils/textHelper';
import { AiGeneratedInlineMark, AI_GENERATED_MARK_ON_KP_QA_ENABLED } from './AiGeneratedContentBadge';
import { DocumentMathTextBlock } from './MathTextBlock';
import {
  buildKpQaExplosionModel,
  type KpExplosionSegment,
  type KpQaExplosionModel,
} from '../utils/knowledgeParticleExplosionSegments';
import {
  type AiChatSegmentAnchor,
} from '../utils/aiChatSegmentKey';

export type QaSegmentSelectionController = {
  selectedObjectIds: string[];
  /** @deprecated KP 已改为颗粒级单图标；TTQ 仍可按段使用 */
  chatAnchors?: AiChatSegmentAnchor[];
  /** 该颗粒最近一次对话（颗粒级单图标续聊） */
  particleChatThreadId?: string | null;
  onToggleSegment: (item: { objectId: string; text: string; particleId: string }) => void;
  onOpenChatThread?: (threadId: string) => void;
};

export type KnowledgeParticleQaDisplay = {
  questionDisplay: { main: string; items: string[] };
  answerDisplay: { main: string; items: string[] };
};

function splitQuestionSegments(value: string) {
  const normalized = value.replace(/\r\n/g, '\n').trim();
  if (!normalized) {
    return [];
  }

  /** 「五(1)班」「高一(2)班」等不是子问题序号 */
  const looksLikeClassOrRoom = (text: string) => (
    /[（(]\s*[一二三四五六七八九十0-9]+\s*[）)]\s*(?:班|年级|组|室|号|栋|楼)/.test(text)
    || /^\s*\d+\s*(?:班|年级|组)/.test(text)
  );

  // 含班级标记的整段题干不要再按句号拆成「子问题」
  if (looksLikeClassOrRoom(normalized) && !/\n/.test(normalized)) {
    return [normalized];
  }

  const lineSegments = normalized
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .flatMap((line) => {
      if (looksLikeClassOrRoom(line)) {
        return [line];
      }
      return line
        .split(/(?=\s*(?:\d+[.)、]|[（(][一二三四五六七八九十0-9]+[）)]|[一二三四五六七八九十]+[、.]))/)
        .map((part) => {
          if (looksLikeClassOrRoom(part)) {
            return part.trim();
          }
          return part
            .replace(/^\s*(?:\d+[.)、]|[（(][一二三四五六七八九十0-9]+[）)]|[一二三四五六七八九十]+[、.])\s*/, '')
            .trim();
        })
        .filter(Boolean);
    });

  if (lineSegments.length > 1) {
    return lineSegments;
  }

  // 不要按句号拆连续题干（否则「……美术书。五(1)班……」会被当成子题 1、2）
  return [normalized];
}

function isLikelySubQuestionText(value: string) {
  const text = value.trim();
  if (!text) {
    return false;
  }

  return /(为什么|为何|请说明|说明|解释|分析|证明|判断|指出|若|如果|误将|误认为|分类法|分类|排除|不能|需排除|易错|考点|步骤|思路|方法|追问|拓展|变式|补充)/.test(text);
}

function compactQuestionDisplay(main: string, items: string[]) {
  const segments = splitQuestionSegments(main);
  if (segments.length <= 1) {
    return { main, items };
  }

  const mainSegments: string[] = [];
  const movedItems: string[] = [];
  segments.forEach((segment, index) => {
    const normalized = segment.trim();
    if (!normalized) {
      return;
    }

    if (index === 0 && !isLikelySubQuestionText(normalized)) {
      mainSegments.push(normalized);
      return;
    }

    movedItems.push(normalized);
  });

  const nextMain = mainSegments.join('\n').trim() || main;
  const seen = new Set<string>();
  const nextItems = [...movedItems, ...items].filter((item) => {
    const key = item.replace(/\s+/g, '');
    if (!key || key === nextMain.replace(/\s+/g, '') || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });

  return { main: nextMain, items: nextItems };
}

export function resolveKnowledgeParticleQaDisplay({
  questionSource,
  answerSource,
  fallbackQuestionText,
}: {
  questionSource: unknown;
  answerSource: unknown;
  fallbackQuestionText?: string;
}): KnowledgeParticleQaDisplay {
  const questionParts = extractStructuredTextParts(questionSource, 'question');
  const hasQuestionContent = Boolean(questionParts.main || questionParts.items.length);
  const effectiveQuestionSource = hasQuestionContent ? questionSource : fallbackQuestionText;
  const resolvedQuestionParts = hasQuestionContent
    ? questionParts
    : extractStructuredTextParts(effectiveQuestionSource, 'question');
  const answerParts = extractStructuredTextParts(answerSource, 'answer');
  const hasMainQuestion = hasExplicitStructuredMain(effectiveQuestionSource, 'question');
  const hasMainAnswer = hasExplicitStructuredMain(answerSource, 'answer');

  return {
    questionDisplay: hasMainQuestion
      ? (resolvedQuestionParts.items.length > 0
        ? {
            main: resolvedQuestionParts.main,
            items: resolvedQuestionParts.items,
          }
        : compactQuestionDisplay(resolvedQuestionParts.main, resolvedQuestionParts.items))
      : {
          main: '',
          items: [resolvedQuestionParts.main, ...resolvedQuestionParts.items].filter(Boolean),
        },
    answerDisplay: hasMainAnswer
      ? {
          main: answerParts.main,
          items: answerParts.items,
        }
      : {
          main: '',
          items: [answerParts.main, ...answerParts.items].filter(Boolean),
        },
  };
}

type KnowledgeParticleQuestionRichBodyProps = {
  questionDisplay: KnowledgeParticleQaDisplay['questionDisplay'];
  questionEmptyText?: string;
  questionOverride?: ReactNode;
  segmentSelection?: QaSegmentSelectionController | null;
  particleId?: string;
};

/** 问题正文：小四宋体 + 罗马体，子问题带序号 */
export function KnowledgeParticleQuestionRichBody({
  questionDisplay,
  questionEmptyText = '暂无内容',
  questionOverride,
  segmentSelection = null,
  particleId = '',
}: KnowledgeParticleQuestionRichBodyProps) {
  const selectionEnabled = Boolean(segmentSelection && particleId);
  const questionExplosion = useMemo(
    () => (particleId ? buildKpQaExplosionModel(particleId, questionDisplay, 'question') : null),
    [particleId, questionDisplay],
  );

  if (questionOverride) {
    return <>{questionOverride}</>;
  }

  if (selectionEnabled && questionExplosion && segmentSelection) {
    return (
      <KpInlineExplosionBody
        model={questionExplosion}
        segmentSelection={segmentSelection}
        particleId={particleId}
        emptyText={questionEmptyText}
      />
    );
  }

  return (
    <>
      {questionDisplay.main ? (
        <div className="ai-chat-assistant-body">
          <DocumentMathTextBlock text={questionDisplay.main} />
          {AI_GENERATED_MARK_ON_KP_QA_ENABLED ? (
            <AiGeneratedInlineMark baseFontSize={14} />
          ) : null}
        </div>
      ) : null}
      {questionDisplay.items.map((item, index) => (
        <div key={`question-${index}`} className="flex gap-3">
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
            {index + 1}
          </span>
          <div className="ai-chat-assistant-body flex-1">
            <DocumentMathTextBlock text={item} />
            {AI_GENERATED_MARK_ON_KP_QA_ENABLED ? (
              <AiGeneratedInlineMark baseFontSize={14} />
            ) : null}
          </div>
        </div>
      ))}
      {!questionDisplay.main && !questionDisplay.items.length ? (
        <p className="text-muted">{questionEmptyText}</p>
      ) : null}
    </>
  );
}

type KnowledgeParticleAnswerRichBodyProps = {
  answerDisplay: KnowledgeParticleQaDisplay['answerDisplay'];
  answerEmptyText?: string;
  segmentSelection?: QaSegmentSelectionController | null;
  particleId?: string;
};

function KpInlineExplosionText({
  segments,
  selectedObjectIds,
  particleId,
  onToggleSegment,
  showAiMark = false,
}: {
  segments: KpExplosionSegment[];
  selectedObjectIds: string[];
  particleId: string;
  onToggleSegment: (item: { objectId: string; text: string; particleId: string }) => void;
  showAiMark?: boolean;
}) {
  if (segments.length === 0) return null;

  const selectedSet = new Set(selectedObjectIds);
  return (
    <div className="document-math-text text-[16px] leading-[1.65] text-ink">
      {segments.map((segment) => {
        const selected = selectedSet.has(segment.objectId);
        return (
          <span
            key={segment.objectId}
            role="button"
            tabIndex={0}
            data-ttq-segment="true"
            data-object-id={segment.objectId}
            aria-pressed={selected}
            className={
              `ttq-explosion-segment inline px-0.5 align-baseline `
              + (selected ? 'is-selected ' : '')
            }
            onClick={() => onToggleSegment({
              objectId: segment.objectId,
              text: segment.text,
              particleId,
            })}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onToggleSegment({
                  objectId: segment.objectId,
                  text: segment.text,
                  particleId,
                });
              }
            }}
          >
            <DocumentMathTextBlock text={segment.text} inline />
          </span>
        );
      })}
      {showAiMark && AI_GENERATED_MARK_ON_KP_QA_ENABLED ? (
        <AiGeneratedInlineMark baseFontSize={14} />
      ) : null}
    </div>
  );
}

function KpInlineExplosionBody({
  model,
  segmentSelection,
  particleId,
  emptyText,
}: {
  model: KpQaExplosionModel;
  segmentSelection: QaSegmentSelectionController;
  particleId: string;
  emptyText: string;
}) {
  const hasContent = model.mainSegments.length > 0 || model.subGroups.length > 0;
  if (!hasContent) {
    return <p className="text-muted">{emptyText}</p>;
  }

  return (
    <>
      <KpInlineExplosionText
        segments={model.mainSegments}
        selectedObjectIds={segmentSelection.selectedObjectIds}
        particleId={particleId}
        onToggleSegment={segmentSelection.onToggleSegment}
        showAiMark={model.mainSegments.length > 0}
      />
      {model.subGroups.map((group) => (
        <div key={`sub-${group.order}`} className="flex gap-3">
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
            {group.order}
          </span>
          <div className="flex-1">
            <KpInlineExplosionText
              segments={group.segments}
              selectedObjectIds={segmentSelection.selectedObjectIds}
              particleId={particleId}
              onToggleSegment={segmentSelection.onToggleSegment}
              showAiMark
            />
          </div>
        </div>
      ))}
    </>
  );
}

/** 答案正文：小四宋体 + 罗马体，子答案带序号 */
export function KnowledgeParticleAnswerRichBody({
  answerDisplay,
  answerEmptyText = '暂无内容',
  segmentSelection = null,
  particleId = '',
}: KnowledgeParticleAnswerRichBodyProps) {
  const selectionEnabled = Boolean(segmentSelection && particleId);
  const answerExplosion = useMemo(
    () => (particleId ? buildKpQaExplosionModel(particleId, answerDisplay, 'answer') : null),
    [answerDisplay, particleId],
  );

  if (selectionEnabled && answerExplosion && segmentSelection) {
    return (
      <KpInlineExplosionBody
        model={answerExplosion}
        segmentSelection={segmentSelection}
        particleId={particleId}
        emptyText={answerEmptyText}
      />
    );
  }

  return (
    <>
      {answerDisplay.main ? (
        <div className="ai-chat-assistant-body">
          <DocumentMathTextBlock text={answerDisplay.main} />
          {AI_GENERATED_MARK_ON_KP_QA_ENABLED ? (
            <AiGeneratedInlineMark baseFontSize={14} />
          ) : null}
        </div>
      ) : null}
      {answerDisplay.items.map((item, index) => (
        <div key={`answer-${index}`} className="flex gap-3">
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-surface text-xs font-medium text-primary">
            {index + 1}
          </span>
          <div className="ai-chat-assistant-body flex-1">
            <DocumentMathTextBlock text={item} />
            {AI_GENERATED_MARK_ON_KP_QA_ENABLED ? (
              <AiGeneratedInlineMark baseFontSize={14} />
            ) : null}
          </div>
        </div>
      ))}
      {!answerDisplay.main && !answerDisplay.items.length ? (
        <p className="text-muted">{answerEmptyText}</p>
      ) : null}
    </>
  );
}

type KnowledgeParticleQaSectionsProps = {
  particle: KnowledgeParticle | null | undefined;
  headline?: string;
  metaText?: string;
  fallbackQuestionText?: string;
  questionEmptyText?: string;
  answerEmptyText?: string;
  /** 替换默认问题正文（如 GO-ON 爆炸选区） */
  questionOverride?: ReactNode;
  /** 在现有问答正文上启用内联选段 */
  segmentSelection?: QaSegmentSelectionController | null;
  showQuestion?: boolean;
  showAnswer?: boolean;
};

export function KnowledgeParticleQaSections({
  particle,
  headline,
  metaText,
  fallbackQuestionText,
  questionEmptyText = '暂无内容',
  answerEmptyText = '暂无内容',
  questionOverride,
  segmentSelection = null,
  showQuestion = true,
  showAnswer = true,
}: KnowledgeParticleQaSectionsProps) {
  const { questionDisplay, answerDisplay } = resolveKnowledgeParticleQaDisplay({
    questionSource: particle?.questions,
    answerSource: particle?.answers,
    fallbackQuestionText,
  });
  const particleId = particle?.id || '';
  const particleChatThreadId = String(segmentSelection?.particleChatThreadId || '').trim();
  const showParticleChatIcon = Boolean(
    particleChatThreadId
    && segmentSelection?.onOpenChatThread,
  );

  return (
    <div className="space-y-4">
      {(headline || metaText) ? (
        <div className="rounded-xl border border-border bg-canvas p-4">
          {headline ? (
            <h2 className="text-lg font-semibold text-ink">
              <DocumentMathTextBlock text={headline} />
            </h2>
          ) : null}
          {metaText ? <p className="mt-1 text-sm text-muted">{metaText}</p> : null}
        </div>
      ) : null}

      {showQuestion ? (
        <section className="rounded-xl border border-border bg-surface p-5 shadow-card">
          <h3 className="flex items-center gap-2 font-medium text-ink">
            <HelpCircle size={16} className="shrink-0 text-primary" aria-hidden />
            <span className="flex-1">问题</span>
            {showParticleChatIcon ? (
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary hover:bg-primary/15"
                title="查看知识颗粒对话"
                aria-label="查看知识颗粒对话"
                onClick={() => segmentSelection?.onOpenChatThread?.(particleChatThreadId)}
              >
                <MessagesSquare size={16} strokeWidth={2.2} />
              </button>
            ) : null}
          </h3>
          <div className="mt-3 space-y-3 text-sm text-ink">
            <KnowledgeParticleQuestionRichBody
              questionDisplay={questionDisplay}
              questionEmptyText={questionEmptyText}
              questionOverride={questionOverride}
              segmentSelection={segmentSelection}
              particleId={particleId}
            />
          </div>
        </section>
      ) : null}

      {showAnswer ? (
        <section className="rounded-xl border border-primary/20 bg-primary/5 p-5 shadow-card">
          <h3 className="flex items-center gap-2 font-medium text-ink">
            <Check size={16} className="shrink-0 text-primary" aria-hidden />
            答案
          </h3>
          <div className="mt-3 space-y-3 text-sm text-ink">
            <KnowledgeParticleAnswerRichBody
              answerDisplay={answerDisplay}
              answerEmptyText={answerEmptyText}
              segmentSelection={segmentSelection}
              particleId={particleId}
            />
          </div>
        </section>
      ) : null}
    </div>
  );
}
